import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { leadsEndpoints } from "@/config/endpoints";

// ---------------------------------------------------------------------------
// LeadPicker — "who should the AI call?" The telecaller switch from the voice
// overlay or the panel arrives without a lead; this finds one by name or
// phone through the ordinary leads list (LeadScope-gated, same rows the
// Leads page shows), then hands id + name to the setup sheet.
// ---------------------------------------------------------------------------

interface PickedLead {
  id: number;
  name: string;
  phone?: string | null;
  stage?: { name?: string } | null;
  status?: { name?: string } | null;
  branch?: { name?: string } | null;
}

interface Props {
  language: "ml" | "en";
  onPick: (lead: { id: number; name: string }) => void;
  onCancel: () => void;
}

const MIN_TERM = 2;
const LIMIT = 8;

const field: React.CSSProperties = {
  width: "100%", height: 44, padding: "0 14px", borderRadius: 14, font: "inherit", fontSize: 15,
  color: "inherit", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)", outline: "none",
};

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

export default function LeadPicker({ language, onPick, onCancel }: Props) {
  const ml = language === "ml";
  const [term, setTerm] = useState("");
  const debounced = useDebounced(term.trim(), 250);
  const canRun = debounced.length >= MIN_TERM;

  const query = useQuery({
    queryKey: ["ai-telecaller-lead-picker", debounced],
    enabled: canRun,
    staleTime: 30_000,
    retry: 0,
    queryFn: async (): Promise<PickedLead[]> => {
      const res = await axiosInstance.get(leadsEndpoints, { params: { search: debounced, page: 1, limit: LIMIT } });
      const rows = (res.data?.leads ?? res.data?.data ?? []) as any[];
      return rows
        .map((r) => ({
          id: Number(r?.id),
          name: String(r?.name ?? ""),
          phone: r?.phone ?? null,
          stage: r?.stage ?? null,
          status: r?.status ?? null,
          branch: r?.branch ?? null,
        }))
        .filter((r) => Number.isFinite(r.id) && r.id > 0 && r.name);
    },
  });

  const results = query.data ?? [];

  return (
    <div className="xv-rec" style={{ display: "block", maxWidth: 560 }}>
      <div className="xv-rec-top" style={{ alignItems: "flex-start" }}>
        <div className="xv-rec-meta" style={{ flex: 1, minWidth: 0 }}>
          <div className="xv-rec-title">{ml ? "AI ആരെ വിളിക്കണം?" : "Who should the AI call?"}</div>
          <div className="xv-rec-sub">{ml ? "പേര് അല്ലെങ്കിൽ ഫോൺ നമ്പർ തിരയൂ — Leads page-ൽ കാണുന്ന അതേ ലീഡുകൾ." : "Search by name or phone — the same leads you see on the Leads page."}</div>
        </div>
        <button className="xv-pill is-icon" onClick={onCancel} aria-label={ml ? "അടയ്ക്കുക" : "Close"} title={ml ? "അടയ്ക്കുക" : "Close"}>
          ×
        </button>
      </div>
      <input
        autoFocus
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && results.length === 1) onPick({ id: results[0].id, name: results[0].name });
          if (e.key === "Escape") onCancel();
        }}
        placeholder={ml ? "ഉദാ: Arjun, 98470…" : "e.g. Arjun, 98470…"}
        style={field}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 300, overflowY: "auto" }}>
        {!canRun && <div className="xv-rec-sub">{ml ? "രണ്ട് അക്ഷരമെങ്കിലും ടൈപ്പ് ചെയ്യൂ." : "Type at least two characters."}</div>}
        {canRun && query.isFetching && results.length === 0 && <div className="xv-rec-sub">{ml ? "തിരയുന്നു…" : "Searching…"}</div>}
        {canRun && !query.isFetching && results.length === 0 && (
          <div className="xv-rec-sub">{query.isError ? (ml ? "തിരയാനായില്ല — വീണ്ടും ശ്രമിക്കൂ." : "Search failed — try again.") : ml ? "ലീഡ് കണ്ടില്ല." : "No lead found."}</div>
        )}
        {results.map((r) => (
          <button
            key={r.id}
            onClick={() => onPick({ id: r.id, name: r.name })}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", cursor: "pointer",
              padding: "9px 12px", borderRadius: 12, color: "inherit", font: "inherit",
              background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <span style={{ width: 30, height: 30, flex: "none", borderRadius: "50%", display: "grid", placeItems: "center", background: "rgba(111,185,156,0.22)", fontSize: 13, fontWeight: 600 }}>
              {r.name.charAt(0).toUpperCase()}
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ display: "block", fontSize: 14, fontWeight: 550, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</span>
              <span className="xv-rec-sub" style={{ display: "block", marginTop: 1 }}>
                {[r.stage?.name, r.status?.name].filter(Boolean).join(" › ")}
                {r.branch?.name ? ` · ${r.branch.name}` : ""}
                {r.phone ? ` · ${String(r.phone).replace(/^(.*)(\d{4})$/, "•••$2")}` : ""}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
